// Foreign Accents Bespoke: a line drawing of a piece that redraws at any width.
const PIECES = {
  table: { min: 36, max: 120, start: 72, h: 30 },
  console: { min: 30, max: 84, start: 54, h: 34 },
  door: { min: 28, max: 48, start: 36, h: 84 },
};
const VB = { left: 110, right: 770, top: 60, floor: 420 };
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

for (const root of document.querySelectorAll('[data-size-sketch]')) {
  // The script tag can appear more than once on a page; set each drawing up once
  if (root.dataset.sketchReady) continue;
  root.dataset.sketchReady = 'true';
  const svg = root.querySelector('[data-size-sketch-svg]');
  const g = root.querySelector('[data-size-sketch-draw]');
  const range = root.querySelector('[data-size-sketch-range]');
  const out = root.querySelector('[data-size-sketch-out]');
  const tabs = [...root.querySelectorAll('[data-piece]')];
  if (!svg || !g || !range || !tabs.length) continue;

  const unit = root.dataset.unit || 'in';
  const label = svg.getAttribute('aria-label') || '';
  let piece = (tabs.find((t) => t.getAttribute('aria-pressed') === 'true') || tabs[0]).dataset.piece;
  let width = PIECES[piece].start;
  const geo = { scale: 1, x0: 0 };

  const pieceName = () => (tabs.find((t) => t.dataset.piece === piece)?.textContent || '').trim();

  function draw(animate) {
    const P = PIECES[piece];
    const s = Math.min((VB.right - VB.left) / P.max, (VB.floor - VB.top) / P.h);
    const W = width * s;
    const H = P.h * s;
    const x0 = VB.left + (VB.right - VB.left - P.max * s) / 2;
    const x1 = x0 + W;
    const yF = Math.round(VB.top + (VB.floor - VB.top + H) / 2);
    const yT = yF - H;
    const inch = (n) => n * s;
    const parts = [];
    let k = 0;
    const path = (d, cls = 'sk-line') => parts.push(`<path class="${cls}" pathLength="1" style="--k:${k++}" d="${d}"/>`);
    const line = (xa, ya, xb, yb, cls) => path(`M${xa.toFixed(1)} ${ya.toFixed(1)}L${xb.toFixed(1)} ${yb.toFixed(1)}`, cls);
    const box = (x, y, w, h, cls) => path(`M${x.toFixed(1)} ${y.toFixed(1)}h${w.toFixed(1)}v${h.toFixed(1)}h${(-w).toFixed(1)}Z`, cls);
    const knob = (cx, cy, r, cls = 'sk-line') => path(`M${(cx + r).toFixed(1)} ${cy.toFixed(1)}a${r} ${r} 0 1 1 0 0.01`, cls);
    const grain = (x, y, w, h, gap) => {
      let d = '';
      for (let t = gap; t < w; t += gap) d += `M${(x + t).toFixed(1)} ${(y + 2).toFixed(1)}l${(-gap * 0.6).toFixed(1)} ${(h - 4).toFixed(1)}`;
      if (d) path(d, 'sk-thin');
    };
    const text = (x, y, t, anchor = 'middle', extra = '') =>
      parts.push(`<text class="sk-text" x="${x.toFixed(1)}" y="${y.toFixed(1)}" text-anchor="${anchor}"${extra}>${t}</text>`);

    line(VB.left - 60, yF, VB.right + 20, yF, 'sk-thin');
    if (piece === 'table') {
      const top = inch(1.75), leg = inch(3), apron = inch(4), inset = inch(2.5);
      box(x0, yT, W, top);
      grain(x0, yT, W, top, 14);
      box(x0 + inset, yT + top, leg, H - top);
      box(x1 - inset - leg, yT + top, leg, H - top);
      box(x0 + inset + leg, yT + top, W - 2 * (inset + leg), apron);
      if (width > 84) box(x0 + W / 2 - leg / 2, yT + top + apron, leg, H - top - apron, 'sk-ghost');
    } else if (piece === 'console') {
      const top = inch(1.5), leg = inch(2.25), caseH = inch(11), shelf = inch(7);
      const drawers = width < 44 ? 2 : width < 66 ? 3 : 4;
      const cx0 = x0 + inch(1), cw = W - inch(2), dw = cw / drawers;
      box(x0, yT, W, top);
      grain(x0, yT, W, top, 14);
      box(cx0, yT + top, cw, caseH);
      for (let n = 0; n < drawers; n++) {
        const dx = cx0 + n * dw;
        box(dx + inch(0.75), yT + top + inch(1.25), dw - inch(1.5), caseH - inch(2.5), 'sk-ghost');
        knob(dx + dw / 2, yT + top + caseH / 2, Math.max(3, inch(0.6)));
      }
      box(cx0, yT + top + caseH, leg, H - top - caseH);
      box(cx0 + cw - leg, yT + top + caseH, leg, H - top - caseH);
      box(cx0 + leg, yF - shelf - inch(1.25), cw - 2 * leg, inch(1.25), 'sk-ghost');
    } else {
      const r = W / 2, frame = inch(2.25), yArc = yT + r, ir = r - frame;
      path(`M${x0.toFixed(1)} ${yF}V${yArc.toFixed(1)}A${r.toFixed(1)} ${r.toFixed(1)} 0 0 1 ${x1.toFixed(1)} ${yArc.toFixed(1)}V${yF}`);
      path(`M${(x0 + frame).toFixed(1)} ${yF}V${yArc.toFixed(1)}A${ir.toFixed(1)} ${ir.toFixed(1)} 0 0 1 ${(x1 - frame).toFixed(1)} ${yArc.toFixed(1)}V${yF}`, 'sk-ghost');
      const pw = (W - frame * 2 - inch(2)) / 2, panelTop = yArc + inch(4), ph = (yF - inch(4) - panelTop - inch(4)) / 3;
      for (let row = 0; row < 3; row++) {
        for (let col = 0; col < 2; col++) box(x0 + frame + inch(0.5) + col * (pw + inch(1)), panelTop + row * (ph + inch(2)), pw, ph, 'sk-ghost');
      }
      // Knob at a usual handle height, a little in from the edge
      knob(x1 - frame - Math.max(inch(5), W * 0.15), yF - inch(36), inch(1.3), 'sk-knob');
    }

    // Measurements
    const yD = yF + 46;
    line(x0, yF + 10, x0, yD + 12, 'sk-dim');
    line(x1, yF + 10, x1, yD + 12, 'sk-dim');
    line(x0, yD, x1, yD, 'sk-dim');
    path(`M${x0 + 8} ${yD - 5}L${x0} ${yD}L${x0 + 8} ${yD + 5}M${x1 - 8} ${yD - 5}L${x1} ${yD}L${x1 - 8} ${yD + 5}`, 'sk-dim');
    text((x0 + x1) / 2, yD - 12, `${width} ${unit}`);
    const xH = x0 - 34;
    line(xH, yT, xH, yF, 'sk-dim');
    path(`M${xH - 5} ${yT + 8}L${xH} ${yT}L${xH + 5} ${yT + 8}M${xH - 5} ${yF - 8}L${xH} ${yF}L${xH + 5} ${yF - 8}`, 'sk-dim');
    const my = (yT + yF) / 2;
    text(xH - 12, my, `${P.h} ${unit}`, 'middle', ` transform="rotate(-90 ${(xH - 12).toFixed(1)} ${my.toFixed(1)})"`);

    // The edge you can drag
    parts.push(`<rect class="sk-handle-hit" data-sk-handle x="${x1 - 16}" y="${yT}" width="32" height="${yD - yT + 20}"/>`);
    parts.push(`<circle class="sk-handle" data-sk-handle cx="${x1}" cy="${yD}" r="8"/>`);

    g.classList.remove('sk-in');
    g.innerHTML = parts.join('');
    if (animate && !reduceMotion.matches) {
      void g.getBoundingClientRect();
      g.classList.add('sk-in');
    }
    if (out) out.textContent = `${width} ${unit}`;
    svg.setAttribute('aria-label', `${label} (${pieceName()}, ${width} × ${P.h} ${unit})`);
    geo.scale = s;
    geo.x0 = x0;
  }

  function choose(p, animate) {
    piece = p;
    const P = PIECES[p];
    width = P.start;
    range.min = P.min;
    range.max = P.max;
    range.value = width;
    tabs.forEach((t) => t.setAttribute('aria-pressed', String(t.dataset.piece === p)));
    draw(animate);
  }

  tabs.forEach((t) => t.addEventListener('click', () => choose(t.dataset.piece, true)));
  range.addEventListener('input', () => {
    width = parseInt(range.value, 10);
    draw(false);
  });

  // Dragging the right edge of the drawing
  let dragging = false;
  const toSvgX = (clientX) => {
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = 0;
    return pt.matrixTransform(svg.getScreenCTM().inverse()).x;
  };
  svg.addEventListener('pointerdown', (e) => {
    if (!e.target.closest('[data-sk-handle]')) return;
    dragging = true;
    svg.setPointerCapture(e.pointerId);
    e.preventDefault();
  });
  svg.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    const P = PIECES[piece];
    const w = Math.min(P.max, Math.max(P.min, Math.round((toSvgX(e.clientX) - geo.x0) / geo.scale)));
    if (w !== width) {
      width = w;
      range.value = w;
      draw(false);
    }
  });
  const stop = () => { dragging = false; };
  svg.addEventListener('pointerup', stop);
  svg.addEventListener('pointercancel', stop);

  choose(piece, false);
  // Draw the lines in when the drawing first comes into view
  if ('IntersectionObserver' in window && !reduceMotion.matches) {
    g.style.visibility = 'hidden';
    const seen = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      seen.disconnect();
      g.style.visibility = '';
      draw(true);
    }, { threshold: 0.3 });
    seen.observe(svg);
  }
}
